import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trash2, Upload, FileText, User, Building, Hash, MapPin, Calendar, Check } from "lucide-react";
import { toast } from "@/components/ui/sonner";

interface NewCertificateFormProps {
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

const NewCertificateForm: React.FC<NewCertificateFormProps> = ({ onSubmit, onCancel }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [certificateText, setCertificateText] = useState<string>("");
  const [formData, setFormData] = useState({
    nomeTitular: "",
    organizacao: "",
    projeto: "",
    tipoCredito: "VCS (Padrão de Carbono Verificado)",
    quantidadeCreditos: "",
    periodoValidade: "01/01/2024 a 11/12/2025",
    cidade: "",
    estado: "",
    descricao: ""
  });
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedTemplate(file.name);
      toast(`Modelo selecionado: ${file.name}`);
    }
  };

  const handleDeleteTemplate = () => {
    setSelectedTemplate("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    toast("Modelo removido");
  };

  const handleTextChange = (content: string) => {
    setCertificateText(content);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = () => {
    // Validar campos obrigatórios
    if (!formData.nomeTitular || !formData.organizacao || !formData.projeto) {
      toast("Por favor, preencha todos os campos obrigatórios");
      return;
    }

    const certificateData = {
      ...formData,
      template: selectedTemplate,
      certificateText: certificateText
    };

    onSubmit(certificateData);
  };

  return (
    <div className="space-y-6">
      {/* Seção Certificado PDF */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-800">
            Certificado PDF
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Input
                value={selectedTemplate}
                placeholder="Selecione o modelo desejado"
                readOnly
                className="w-full"
                onClick={triggerFileInput}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={triggerFileInput}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Selecionar
            </Button>
            {selectedTemplate && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteTemplate}
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Seção Texto Certificado */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-800">
            Texto Certificado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RichTextEditor
            value={certificateText}
            onChange={handleTextChange}
          />
        </CardContent>
      </Card>

      {/* Seção Novo Certificado */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-800">
            Novo Certificado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Coluna Esquerda */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do Titular
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={formData.nomeTitular}
                    onChange={(e) => handleInputChange('nomeTitular', e.target.value)}
                    placeholder="Nome da pessoa ou entidade"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Projeto
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={formData.projeto}
                    onChange={(e) => handleInputChange('projeto', e.target.value)}
                    placeholder="Nome do projeto"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantidade de Créditos [KgCO₂e]
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={formData.quantidadeCreditos}
                    onChange={(e) => handleInputChange('quantidadeCreditos', e.target.value)}
                    placeholder="Quantidade em KgCO₂e"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cidade
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={formData.cidade}
                    onChange={(e) => handleInputChange('cidade', e.target.value)}
                    placeholder="Cidade"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <Textarea
                  value={formData.descricao}
                  onChange={(e) => handleInputChange('descricao', e.target.value)}
                  placeholder="Descrição específica do certificado e do projeto associado"
                  className="min-h-[100px]"
                />
              </div>
            </div>

            {/* Coluna Direita */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organização
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={formData.organizacao}
                    onChange={(e) => handleInputChange('organizacao', e.target.value)}
                    placeholder="Nome da organização"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Crédito
                </label>
                <Input
                  value={formData.tipoCredito}
                  onChange={(e) => handleInputChange('tipoCredito', e.target.value)}
                  className="bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Período de Validade
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={formData.periodoValidade}
                    onChange={(e) => handleInputChange('periodoValidade', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={formData.estado}
                    onChange={(e) => handleInputChange('estado', e.target.value)}
                    placeholder="Estado (UF)"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit}
              className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white flex items-center gap-2"
            >
              <Check className="h-4 w-4" />
              Emitir Certificado
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Rich Text Editor Component
const RichTextEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);

  const handleFormat = (format: string) => {
    switch (format) {
      case 'bold':
        setIsBold(!isBold);
        break;
      case 'italic':
        setIsItalic(!isItalic);
        break;
      case 'underline':
        setIsUnderline(!isUnderline);
        break;
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-200 p-2">
        {/* First Row */}
        <div className="flex items-center gap-1 mb-2">
          <select className="px-2 py-1 text-sm border border-gray-300 rounded mr-2">
            <option>Sans Serif</option>
            <option>Serif</option>
            <option>Monospace</option>
          </select>
          <select className="px-2 py-1 text-sm border border-gray-300 rounded mr-2">
            <option>Normal</option>
            <option>Small</option>
            <option>Large</option>
          </select>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </Button>
          <Button 
            variant={isBold ? "default" : "ghost"} 
            size="sm" 
            className="h-8 w-8 p-0 font-bold"
            onClick={() => handleFormat('bold')}
          >
            B
          </Button>
          <Button 
            variant={isItalic ? "default" : "ghost"} 
            size="sm" 
            className="h-8 w-8 p-0 italic"
            onClick={() => handleFormat('italic')}
          >
            I
          </Button>
          <Button 
            variant={isUnderline ? "default" : "ghost"} 
            size="sm" 
            className="h-8 w-8 p-0 underline"
            onClick={() => handleFormat('underline')}
          >
            U
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 13l3 3 7-7" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2M7 4h10M7 4l-1 12h12l-1-12M9 9v6M15 9v6" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2M7 4h10M7 4l-1 12h12l-1-12M9 9v6M15 9v6" />
            </svg>
          </Button>
        </div>
        
        {/* Second Row */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-xs">
            H₁
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-xs">
            H₂
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-xs">
            H₃
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-xs">
            H₄
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2M7 4h10M7 4l-1 12h12l-1-12M9 9v6M15 9v6" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2M7 4h10M7 4l-1 12h12l-1-12M9 9v6M15 9v6" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>
      </div>
      
      {/* Text Area */}
      <div className="p-4">
        <textarea
          value={value}
          onChange={handleTextChange}
          placeholder="Digite o conteúdo do certificado aqui..."
          className="w-full h-64 resize-none border-none outline-none text-sm leading-relaxed"
          style={{
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: isBold ? 'bold' : 'normal',
            fontStyle: isItalic ? 'italic' : 'normal',
            textDecoration: isUnderline ? 'underline' : 'none'
          }}
        />
      </div>
    </div>
  );
};

export default NewCertificateForm;
